import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCartTotalItems } from '../../store/slices/cartSlice';
import Button from '../common/Button';
import { useAuth } from '../../hooks/useAuth';
import ProfileMenu from './ProfileMenu';
import { theme } from '../../config/theme';
import { ShoppingBagIcon, ShoppingCartIcon, SearchIcon, XIcon, PackageIcon, HomeModernIcon, RefreshIcon } from '../icons/Icons';
import WishlistIcon from '../wishlist/WishlistIcon';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { formatCurrency } from '../../utils/helpers';
import Footer from './Footer';
import { productService } from '../../services/product.service';

const PublicLayout = ({ children }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, role } = useAuth();
  const cartItemCount = useSelector(selectCartTotalItems);
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Navbar states
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const searchRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const response = await productService.search(query, {}, { limit: 5 });
        setSearchResults(response.data?.products || response.products || []);
      } catch (error) {
        console.error('Search failed', error);
      } finally {
        setIsSearching(false);
      }
    }, 400); // 400ms debounce
  };

  const navStyles = {
    position: 'sticky',
    top: 0,
    backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.95)' : theme.colors.white,
    backdropFilter: isScrolled ? 'blur(12px)' : 'none',
    borderBottom: isScrolled ? 'none' : `1px solid ${theme.colors.gray[200]}`,
    padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.md} ${theme.spacing.xl}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: theme.zIndex.dropdown,
    boxShadow: isScrolled ? '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)' : 'none',
    transition: 'all 0.3s ease',
  };

  const logoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm, // Adds spacing between image and text
    cursor: 'pointer',
    flexShrink: 0,
    marginRight: theme.spacing.xl,
  };

  const logoTextStyles = {
    fontSize: isMobile ? theme.typography.fontSize.lg : theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    background: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  };

  const navLinksStyles = {
    display: 'flex',
    gap: isMobile ? theme.spacing.sm : theme.spacing.lg,
    alignItems: 'center',
    flexShrink: 0,
  };

  const cartButtonStyles = {
    position: 'relative',
    padding: theme.spacing.sm,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    transition: theme.transitions.base,
  };

  const badgeStyles = {
    position: 'absolute',
    top: '2px',
    right: '2px',
    backgroundColor: theme.colors.error,
    color: theme.colors.white,
    borderRadius: theme.borderRadius.full,
    padding: '2px 6px',
    fontSize: theme.typography.fontSize.xs,
    fontWeight: theme.typography.fontWeight.bold,
    minWidth: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // Search input styles
  const searchContainerStyles = {
    position: 'relative',
    flex: 1,
    maxWidth: '500px',
    margin: '0 auto',
  };

  const searchInputStyles = {
    width: '100%',
    padding: `10px 16px 10px 40px`,
    borderRadius: theme.borderRadius.full,
    border: `1px solid ${isScrolled ? theme.colors.gray[200] : theme.colors.gray[300]}`,
    backgroundColor: isScrolled ? theme.colors.white : theme.colors.gray[50],
    fontSize: theme.typography.fontSize.sm,
    transition: 'all 0.2s',
    outline: 'none',
  };

  const searchDropdownStyles = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '8px',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.lg,
    overflow: 'hidden',
    border: `1px solid ${theme.colors.gray[200]}`,
    zIndex: theme.zIndex.dropdown + 1,
    maxHeight: '400px',
    overflowY: 'auto',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Navbar */}
      <nav style={navStyles}>
        <div style={logoStyles} onClick={() => navigate('/')}>
          <img 
            src="/images/logo.png" 
            alt="AlarcTrade Logo" 
            style={{ 
              height: isMobile ? '28px' : '36px', 
              width: 'auto', 
              objectFit: 'contain' 
            }} 
          />
          <span style={logoTextStyles}>AlarcTrade</span>
        </div>
        
        {(!user || user?.role === 'customer') && (
          <div style={{...searchContainerStyles, display: isMobile ? 'none' : 'block'}} ref={searchRef}>
            <SearchIcon 
              size={18} 
              color={theme.colors.text.secondary} 
              style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} 
            />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => { if(searchQuery) setShowDropdown(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim()) {
                  navigate(`/?search=${encodeURIComponent(searchQuery)}`);
                  setShowDropdown(false);
                }
              }}
              style={searchInputStyles}
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setShowDropdown(false); }}
                style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
              >
                <XIcon size={16} color={theme.colors.text.secondary} />
              </button>
            )}

            {showDropdown && (
              <div style={searchDropdownStyles}>
                {isSearching ? (
                  <div style={{ padding: theme.spacing.lg, textAlign: 'center', color: theme.colors.text.secondary }}>
                    Searching...
                  </div>
                ) : searchResults.length > 0 ? (
                  <div>
                    {searchResults.map(product => (
                      <div 
                        key={product.id}
                        onClick={() => {
                          navigate(`/products/${product.slug || product.id}`);
                          setShowDropdown(false);
                        }}
                        style={{
                          padding: theme.spacing.md,
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing.md,
                          cursor: 'pointer',
                          borderBottom: `1px solid ${theme.colors.gray[100]}`,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.gray[50]}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {product.images?.[0]?.url ? (
                          <img 
                            src={product.images[0].url} 
                            alt={product.name} 
                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: theme.borderRadius.sm }} 
                          />
                        ) : (
                          <div style={{ width: '40px', height: '40px', backgroundColor: theme.colors.gray[100], borderRadius: theme.borderRadius.sm, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PackageIcon size={20} color={theme.colors.gray[400]} />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: theme.typography.fontWeight.medium, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {product.name}
                          </div>
                          <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.primary.main, fontWeight: theme.typography.fontWeight.bold }}>
                            {formatCurrency(product.price)}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div 
                      onClick={() => {
                        navigate(`/?search=${encodeURIComponent(searchQuery)}`);
                        setShowDropdown(false);
                      }}
                      style={{
                        padding: theme.spacing.md,
                        textAlign: 'center',
                        color: theme.colors.primary.main,
                        fontWeight: theme.typography.fontWeight.medium,
                        cursor: 'pointer',
                        borderTop: `1px solid ${theme.colors.gray[100]}`,
                        backgroundColor: theme.colors.gray[50]
                      }}
                    >
                      View all results
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: theme.spacing.lg, textAlign: 'center', color: theme.colors.text.secondary }}>
                    No products found for "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={navLinksStyles}>
          {(!user || user?.role === 'customer') && (
            <button
              style={cartButtonStyles}
              onClick={() => navigate('/rentals')}
              title="Rentals"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.gray[50];
                e.currentTarget.style.borderRadius = theme.borderRadius.md;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <HomeModernIcon size={24} color={theme.colors.text.primary} />
            </button>
          )}
          {(!user || user?.role === 'customer') && (
            <button
              style={cartButtonStyles}
              onClick={() => navigate('/barter')}
              title="Barter"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.gray[50];
                e.currentTarget.style.borderRadius = theme.borderRadius.md;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <RefreshIcon size={24} color={theme.colors.text.primary} />
            </button>
          )}
          {isAuthenticated ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? theme.spacing.md : theme.spacing.lg }}>
              {user?.role === 'customer' && (
                <>
                  <WishlistIcon />
                  <button 
                    style={cartButtonStyles} 
                    onClick={() => navigate('/cart')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.gray[50];
                      e.currentTarget.style.borderRadius = theme.borderRadius.md;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <ShoppingCartIcon size={24} color={theme.colors.text.primary} />
                    {cartItemCount > 0 && (
                      <span style={badgeStyles}>{cartItemCount}</span>
                    )}
                  </button>
                </>
              )}
              <ProfileMenu user={user} isMobile={isMobile} />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: theme.spacing.sm }}>
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/register')}>
                Sign Up
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main style={{ flex: 1 }}>
        {children}
      </main>

      <Footer />
    </div>
  );
};

export default PublicLayout;